import * as THREE from 'three';

const SIZE = 26;
const HALF = SIZE / 2;
const WALL_H = 6;
const CEIL_Y = 5.8;
const FLOOR2_Y = 6.2;
const BLDG_R = 17;
const BLDG_H = 13;
const ENTRANCE_W = 8;
const ENTRANCE_Z = BLDG_R;

export class MuseumStructure {
    constructor(scene, textureLoader) {
        this.scene = scene;
        this.textureLoader = textureLoader;
        this.skyDome = null;
        this.dustParticles = null;
        // Exterior shell group — hidden when camera is inside the gallery
        // so the sky dome renders clean on the second floor
        this.exteriorShell = new THREE.Group();
        this.scene.add(this.exteriorShell);
    }

    createOutdoorGround() {
        const mat = new THREE.MeshStandardMaterial({ color: 0x4a7c3f, roughness: 0.9 });
        const ground = new THREE.Mesh(new THREE.PlaneGeometry(300, 300), mat);
        ground.rotation.x = -Math.PI / 2; ground.position.y = -0.05; ground.receiveShadow = true;
        this.scene.add(ground);
        const path = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 16),
            new THREE.MeshStandardMaterial({ color: 0x9e9688, roughness: 0.7 }));
        path.rotation.x = -Math.PI / 2; path.position.set(0, 0.01, 25);
        this.scene.add(path);
    }

    createOutdoorSky() {
        this.scene.background = new THREE.Color(0x87ceeb);
        this.scene.fog = new THREE.Fog(0x87ceeb, 60, 150);
        const sun = new THREE.DirectionalLight(0xfffae0, 1.5);
        sun.position.set(30, 40, 20); sun.castShadow = true;
        sun.shadow.mapSize.set(2048, 2048);
        sun.shadow.camera.left = -50; sun.shadow.camera.right = 50;
        sun.shadow.camera.top = 50; sun.shadow.camera.bottom = -50;
        this.scene.add(sun);
        this.scene.add(new THREE.AmbientLight(0xb0d0ff, 0.5));
        this.scene.add(new THREE.HemisphereLight(0x87ceeb, 0x4a7c3f, 0.4));
    }

    createTrees() {
        const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5c3a1e, roughness: 0.9 });
        const leafMats = [
            new THREE.MeshStandardMaterial({ color: 0x2d5a27, roughness: 0.8 }),
            new THREE.MeshStandardMaterial({ color: 0x3a6b32, roughness: 0.8 }),
            new THREE.MeshStandardMaterial({ color: 0x1e4d1a, roughness: 0.8 }),
        ];
        const trunkGeo = new THREE.CylinderGeometry(0.2, 0.35, 3, 6);
        const coneGeos = [new THREE.ConeGeometry(2.0,4.5,7), new THREE.ConeGeometry(1.6,3.5,7), new THREE.ConeGeometry(1.2,2.8,7)];
        const addTree = (x, z, s) => {
            const g = new THREE.Group();
            const trunk = new THREE.Mesh(trunkGeo, trunkMat);
            trunk.position.y = 1.5; g.add(trunk);
            const lm = leafMats[Math.floor(Math.random()*3)];
            for (let j=0;j<3;j++) { const c=new THREE.Mesh(coneGeos[j],lm); c.position.y=3.5+j*1.8; c.castShadow=true; g.add(c); }
            g.position.set(x,0,z); g.scale.setScalar(s); g.rotation.y=Math.random()*Math.PI; this.scene.add(g);
        };
        for (let i=0;i<60;i++) { const a=Math.random()*Math.PI*2, d=19+Math.random()*6, x=Math.cos(a)*d, z=Math.sin(a)*d; if(Math.abs(x)<6&&z>14&&z<35) continue; addTree(x,z,0.8+Math.random()*0.6); }
        for (let i=0;i<50;i++) { const a=Math.random()*Math.PI*2, d=26+Math.random()*35, x=Math.cos(a)*d, z=Math.sin(a)*d; if(Math.abs(x)<6&&z>14&&z<40) continue; addTree(x,z,0.7+Math.random()*0.8); }
    }

    createBillboard() {
        const c=document.createElement('canvas'); c.width=1024; c.height=256; const x=c.getContext('2d');
        x.fillStyle='#1a1a2e'; x.fillRect(0,0,1024,256); x.strokeStyle='#c4a87c'; x.lineWidth=6; x.strokeRect(12,12,1000,232);
        x.fillStyle='#f0ebe0'; x.font='bold 36px Georgia,serif'; x.textAlign='center'; x.fillText('WELCOME TO',512,70);
        x.fillStyle='#c4a87c'; x.font='bold 48px Georgia,serif'; x.fillText('THE SCHOOL OF NELSONVILLE',512,135);
        x.fillStyle='#f0ebe0'; x.font='italic 32px Georgia,serif'; x.fillText('William Hughes Museum',512,200);
        const b=new THREE.Mesh(new THREE.PlaneGeometry(8,2), new THREE.MeshBasicMaterial({map:new THREE.CanvasTexture(c)}));
        b.position.set(0,3.5,28); this.scene.add(b);
        const pg=new THREE.CylinderGeometry(0.12,0.12,4,8), pm=new THREE.MeshStandardMaterial({color:0x333333,metalness:0.5,roughness:0.4});
        [-3.5,3.5].forEach(px => { const p=new THREE.Mesh(pg,pm); p.position.set(px,2,28); this.scene.add(p); });
    }

    createBuilding() {
        const bm = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.02 });
        const seg = 64;
        const bigOpenW = ENTRANCE_W * 3;
        const ea = Math.atan2(bigOpenW / 2, BLDG_R) * 2;
        const wa = Math.PI * 2 - ea;
        // All exterior geometry goes into this.exteriorShell
        // so App.js can hide it when the camera is inside the gallery.
        // This guarantees the sky dome renders clean on the second floor.
        const shell = this.exteriorShell;

        // Outer cylinder
        const w = new THREE.Mesh(new THREE.CylinderGeometry(BLDG_R, BLDG_R, BLDG_H, seg, 1, true, ea/2, wa), bm);
        w.position.y = BLDG_H / 2; shell.add(w);

        // Inner cylinder FIRST FLOOR ONLY (visible from inside via scale flip)
        // This stays in the main scene — it gives the entrance corridor
        // its curved wall look and is below the second floor.
        const ig = new THREE.CylinderGeometry(BLDG_R-0.4, BLDG_R-0.4, CEIL_Y, seg, 1, true, ea/2, wa);
        ig.scale(-1, 1, 1);
        const inn = new THREE.Mesh(ig, new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.02 }));
        inn.position.y = CEIL_Y / 2; this.scene.add(inn);

        // === GUGGENHEIM SPIRAL RINGS ===
        const ringMat = new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.3, metalness: 0.05 });
        const ringCount = 6;
        for (let i = 0; i < ringCount; i++) {
            const t = i / (ringCount - 1);
            const ringR = BLDG_R + 0.2 + t * 1.8;
            const tubeR = 0.35 + t * 0.1;
            const r = new THREE.Mesh(new THREE.TorusGeometry(ringR, tubeR, 8, seg, wa), ringMat);
            r.position.y = 1.5 + i * 2.1;
            r.rotation.x = Math.PI / 2;
            r.rotation.z = ea / 2;
            shell.add(r);
        }
        // Cap ring at roofline
        const capMat = new THREE.MeshStandardMaterial({ color: 0xeaeaea, roughness: 0.3, metalness: 0.08 });
        const cap = new THREE.Mesh(new THREE.TorusGeometry(BLDG_R + 2.2, 0.5, 8, seg, wa), capMat);
        cap.position.y = BLDG_H - 0.3;
        cap.rotation.x = Math.PI / 2;
        cap.rotation.z = ea / 2;
        shell.add(cap);

        // === FLAT FRONT FACE with rectangular entrance ===
        const frontMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.02, side: THREE.DoubleSide });
        const panelW = (bigOpenW - ENTRANCE_W) / 2 + 1;
        const lp = new THREE.Mesh(new THREE.BoxGeometry(panelW, BLDG_H, 0.5), frontMat);
        lp.position.set(-(ENTRANCE_W / 2 + panelW / 2), BLDG_H / 2, ENTRANCE_Z);
        shell.add(lp);
        const rp = new THREE.Mesh(new THREE.BoxGeometry(panelW, BLDG_H, 0.5), frontMat);
        rp.position.set((ENTRANCE_W / 2 + panelW / 2), BLDG_H / 2, ENTRANCE_Z);
        shell.add(rp);
        const hh = BLDG_H - WALL_H;
        const hd = new THREE.Mesh(new THREE.BoxGeometry(ENTRANCE_W, hh, 0.5), frontMat);
        hd.position.set(0, WALL_H + hh / 2, ENTRANCE_Z);
        shell.add(hd);
        // Gold frame
        const fm = new THREE.MeshStandardMaterial({ color: 0xc4a87c, roughness: 0.3, metalness: 0.4 });
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(ENTRANCE_W + 0.4, 0.3, 0.6), fm);
        lintel.position.set(0, WALL_H, ENTRANCE_Z + 0.1); shell.add(lintel);
        [-1, 1].forEach(s => {
            const tr = new THREE.Mesh(new THREE.BoxGeometry(0.2, WALL_H, 0.3), fm);
            tr.position.set(s * ENTRANCE_W / 2, WALL_H / 2, ENTRANCE_Z + 0.1); shell.add(tr);
        });

        // Warm glow inside entrance (stays in scene — it's a light, not geometry)
        const entranceLight = new THREE.PointLight(0xffeedd, 1.0, 12);
        entranceLight.position.set(0, 4, ENTRANCE_Z - 2);
        this.scene.add(entranceLight);

        // Roof
        const roofMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.02 });
        const roof = new THREE.Mesh(new THREE.CircleGeometry(BLDG_R + 0.5, seg), roofMat);
        roof.rotation.x = -Math.PI / 2; roof.position.y = BLDG_H;
        shell.add(roof);
    }

    createInteriorFloor() {
        const f = new THREE.Mesh(new THREE.PlaneGeometry(SIZE, SIZE), new THREE.MeshStandardMaterial({ color: 0xe8ddd0, roughness: 0.4, metalness: 0.05 }));
        f.rotation.x = -Math.PI / 2; f.position.y = 0.02; f.receiveShadow = true; this.scene.add(f);
        const cp = new THREE.Mesh(new THREE.PlaneGeometry(2.5, SIZE * 0.8), new THREE.MeshStandardMaterial({ color: 0x8b1a1a, roughness: 0.9 }));
        cp.rotation.x = -Math.PI / 2; cp.position.set(0, 0.04, 2); this.scene.add(cp);
    }

    createFirstFloorCeiling() {
        const mat = new THREE.MeshStandardMaterial({ color: 0xf5f0e8, side: THREE.DoubleSide, roughness: 0.4 });
        const ew = 4, ed = 5, ez = -3;
        const add = (w, d, x, z) => { const p = new THREE.Mesh(new THREE.PlaneGeometry(w, d), mat); p.rotation.x = Math.PI / 2; p.position.set(x, CEIL_Y, z); this.scene.add(p); };
        add(SIZE + 1, HALF - (ez + ed / 2) + 0.5, 0, (HALF + ez + ed / 2) / 2);
        add(SIZE + 1, (ez - ed / 2) + HALF + 0.5, 0, (-HALF + ez - ed / 2) / 2);
        add((SIZE - ew) / 2 + 0.5, ed, -(SIZE + ew) / 4, ez);
        add((SIZE - ew) / 2 + 0.5, ed, (SIZE + ew) / 4, ez);
    }

    createSecondFloor() {
        const gt = this.textureLoader.load('/assets/Textures/Grass/GrassGreenTexture0002.jpg', t => { t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping; t.repeat.set(6, 6); });
        const mat = new THREE.MeshStandardMaterial({ map: gt, roughness: 0.8, metalness: 0.2 });
        const c = new THREE.Mesh(new THREE.CircleGeometry(SIZE * 0.7, 64), mat); c.rotation.x = -Math.PI / 2; c.position.y = FLOOR2_Y; this.scene.add(c);
        const ew = 4, ed = 5, ez = -3;
        const add = (w, d, x, z) => { const p = new THREE.Mesh(new THREE.PlaneGeometry(w, d), mat); p.rotation.x = -Math.PI / 2; p.position.set(x, FLOOR2_Y + 0.01, z); this.scene.add(p); };
        add(SIZE, HALF - (ez + ed / 2), 0, (HALF + ez + ed / 2) / 2);
        add(SIZE, (ez - ed / 2) + HALF, 0, (-HALF + ez - ed / 2) / 2);
        add((SIZE - ew) / 2, ed, -(SIZE + ew) / 4, ez);
        add((SIZE - ew) / 2, ed, (SIZE + ew) / 4, ez);
    }

    createWalls() {
        const wm = new THREE.MeshStandardMaterial({ color: 0xf0ebe0, roughness: 0.6, metalness: 0.02 });
        const rm = new THREE.MeshStandardMaterial({ color: 0xc4a87c, roughness: 0.4, metalness: 0.3 });
        const addW = (x, y, z, w, h, d) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wm); m.position.set(x, y, z); this.scene.add(m); };
        const addR = (x, z, w, d) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, 0.06, d), rm); m.position.set(x, WALL_H * 0.72, z); this.scene.add(m); };
        addW(0, WALL_H / 2, -HALF, SIZE, WALL_H, 0.5);
        addW(-HALF, WALL_H / 2, 0, 0.5, WALL_H, SIZE);
        addW(HALF, WALL_H / 2, 0, 0.5, WALL_H, SIZE);
        addR(0, -HALF + 0.05, SIZE, 0.15);
        addR(-HALF + 0.05, 0, 0.15, SIZE);
        addR(HALF - 0.05, 0, 0.15, SIZE);
        const f2y = FLOOR2_Y + WALL_H / 2;
        addW(0, f2y, -HALF, SIZE, WALL_H, 0.5);
        addW(-HALF, f2y, 0, 0.5, WALL_H, SIZE);
        addW(HALF, f2y, 0, 0.5, WALL_H, SIZE);
        addW(0, f2y, HALF, SIZE, WALL_H, 0.5);
    }

    createInteriorLighting() {
        this.scene.add(new THREE.AmbientLight(0xfff5e6, 0.8));
        const addSpot = (x, z, tx, tz) => {
            const s = new THREE.SpotLight(0xffeedd, 2.5); s.position.set(x, 5.5, z); s.target.position.set(tx, 2.5, tz);
            s.angle = Math.PI / 3; s.penumbra = 0.8; s.decay = 1.2; s.distance = 20; s.castShadow = true; s.shadow.bias = -0.001; s.shadow.mapSize.set(1024, 1024);
            this.scene.add(s); this.scene.add(s.target);
        };
        addSpot(-5, -5, 0, -HALF); addSpot(5, -5, 0, -HALF); addSpot(-5, 5, 0, HALF); addSpot(5, 5, 0, HALF);
        const cl = new THREE.PointLight(0xfff5e6, 0.6, 18); cl.position.set(0, 5.5, 0); this.scene.add(cl);
        const p1 = new THREE.PointLight(0xeeeeff, 0.6, 20); p1.position.set(0, 10, 0); this.scene.add(p1);
    }

    createSecondFloorSky() {
        this.textureLoader.load('/assets/Textures/NightSky/milkyway_night_sky.jpg', (texture) => {
            texture.wrapS = THREE.RepeatWrapping; texture.wrapT = THREE.RepeatWrapping;
            const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide });
            const geo = new THREE.SphereGeometry(16, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
            this.skyDome = new THREE.Mesh(geo, mat);
            this.skyDome.position.y = FLOOR2_Y;
            this.scene.add(this.skyDome);
        });
    }

    createDustParticles() {
        const n = 150, geo = new THREE.BufferGeometry(), pos = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) { pos[i*3] = (Math.random()-0.5)*SIZE*0.8; pos[i*3+1] = Math.random()*WALL_H*0.8+0.5; pos[i*3+2] = (Math.random()-0.5)*SIZE*0.8; }
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        this.dustParticles = new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.03, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false }));
        this.scene.add(this.dustParticles);
    }

    updateDust(time) {
        if (!this.dustParticles) return;
        const p = this.dustParticles.geometry.attributes.position.array;
        for (let i = 0; i < p.length; i += 3) { p[i] += Math.sin(time*0.3+i)*0.002; p[i+1] += Math.sin(time*0.2+i*0.7)*0.001; p[i+2] += Math.cos(time*0.25+i*0.5)*0.002; }
        this.dustParticles.geometry.attributes.position.needsUpdate = true;
    }

    buildAll() {
        this.createOutdoorGround(); this.createOutdoorSky(); this.createTrees(); this.createBillboard();
        this.createBuilding(); this.createInteriorFloor(); this.createFirstFloorCeiling();
        this.createSecondFloor(); this.createWalls(); this.createInteriorLighting();
        this.createSecondFloorSky(); this.createDustParticles();
    }
}
